import { useState, useEffect, useCallback } from 'react'
import { useDebounce } from '@/hooks/use-debounce'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Search, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import { showSuccessToast, showErrorToast } from '@/components/NotificationToast'
import { formatDisplayDate } from '@/lib/dateUtils'
import { fetchClients, type ClientSummary } from '@/services/invoiceService'
import {
  getClassificationRules,
  updateClassificationRule,
  deleteClassificationRule,
  getCounterpartyMappings,
  deleteCounterpartyMapping,
  getClientIdentifications,
  deleteClientIdentification,
  type ClassificationRule,
  type CounterpartyMapping,
  type ClientIdentificationItem,
} from '@/services/learningsService'

const PAGE_SIZE = 20

// ─── Classification Rules Tab ────────────────────────────────────────────────

interface EditRuleForm {
  rule_text: string
  erp_data_json: string
  is_active: boolean
}

function ClassificationRulesTab({ clients }: { clients: ClientSummary[] }) {
  const [rules, setRules] = useState<ClassificationRule[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [clientFilter, setClientFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined)

  const [editingRule, setEditingRule] = useState<ClassificationRule | null>(null)
  const [editForm, setEditForm] = useState<EditRuleForm>({ rule_text: '', erp_data_json: '{}', is_active: true })
  const [saving, setSaving] = useState(false)

  const [deletingRule, setDeletingRule] = useState<ClassificationRule | null>(null)
  const [deleting, setDeleting] = useState(false)

  const loadRules = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getClassificationRules({
        search: debouncedSearch || undefined,
        client_id: clientFilter || undefined,
        is_active: activeFilter,
        page,
        page_size: PAGE_SIZE,
      })
      setRules(data.rules)
      setTotal(data.total)
    } catch (err) {
      showErrorToast('Failed to load classification rules.')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, clientFilter, activeFilter, page])

  useEffect(() => { loadRules() }, [loadRules])

  // Reset page when filters change
  useEffect(() => { setPage(1) }, [debouncedSearch, clientFilter, activeFilter])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const openEdit = (rule: ClassificationRule) => {
    setEditingRule(rule)
    setEditForm({
      rule_text: rule.rule_text,
      erp_data_json: JSON.stringify(rule.erp_data, null, 2),
      is_active: rule.is_active,
    })
  }

  const handleSave = async () => {
    if (!editingRule) return
    let erp_data: Record<string, any>
    try {
      erp_data = JSON.parse(editForm.erp_data_json)
    } catch {
      showErrorToast('Invalid JSON in ERP data field.')
      return
    }
    setSaving(true)
    try {
      await updateClassificationRule(editingRule.id, {
        rule_text: editForm.rule_text,
        erp_data,
        is_active: editForm.is_active,
      })
      showSuccessToast('Classification rule updated.')
      setEditingRule(null)
      loadRules()
    } catch {
      showErrorToast('Failed to update classification rule.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingRule) return
    setDeleting(true)
    try {
      await deleteClassificationRule(deletingRule.id)
      showSuccessToast('Classification rule deleted.')
      setDeletingRule(null)
      loadRules()
    } catch {
      showErrorToast('Failed to delete classification rule.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search rules..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <Label htmlFor="active-filter" className="text-sm text-muted-foreground">Active only</Label>
          <Switch
            id="active-filter"
            checked={activeFilter === true}
            onCheckedChange={(checked) => setActiveFilter(checked ? true : undefined)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-neutral-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Counterparty</TableHead>
              <TableHead>Product Pattern</TableHead>
              <TableHead className="max-w-[250px]">Rule Text</TableHead>
              <TableHead className="text-center">Applied</TableHead>
              <TableHead className="text-center">Confirmed</TableHead>
              <TableHead className="text-center">Overridden</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="h-32 text-center text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : rules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="h-32 text-center text-muted-foreground">
                  No classification rules found.
                </TableCell>
              </TableRow>
            ) : (
              rules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell className="font-medium">{rule.client_name ?? '-'}</TableCell>
                  <TableCell>
                    <div className="text-sm">{rule.counterparty_name_normalized ?? '-'}</div>
                    {rule.counterparty_vat && (
                      <div className="text-xs text-muted-foreground">{rule.counterparty_vat}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{rule.product_pattern ?? '-'}</TableCell>
                  <TableCell className="max-w-[250px] truncate text-sm" title={rule.rule_text}>
                    {rule.rule_text}
                  </TableCell>
                  <TableCell className="text-center tabular-nums">{rule.times_applied}</TableCell>
                  <TableCell className="text-center tabular-nums">{rule.times_confirmed}</TableCell>
                  <TableCell className="text-center tabular-nums">{rule.times_overridden}</TableCell>
                  <TableCell>
                    <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                      {rule.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(rule)} title="Edit rule">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeletingRule(rule)} title="Delete rule">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-3 text-sm tabular-nums">{page} / {totalPages}</span>
            <Button variant="outline" size="icon" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingRule} onOpenChange={(open) => { if (!open) setEditingRule(null) }}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Edit Classification Rule</DialogTitle>
            <DialogDescription>
              Update the rule text, ERP data, or toggle its active status.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-rule-text">Rule Text</Label>
              <Textarea
                id="edit-rule-text"
                rows={4}
                value={editForm.rule_text}
                onChange={(e) => setEditForm({ ...editForm, rule_text: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-erp-data">ERP Data (JSON)</Label>
              <Textarea
                id="edit-erp-data"
                rows={6}
                className="font-mono text-sm"
                value={editForm.erp_data_json}
                onChange={(e) => setEditForm({ ...editForm, erp_data_json: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="edit-is-active"
                checked={editForm.is_active}
                onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
              />
              <Label htmlFor="edit-is-active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingRule(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingRule} onOpenChange={(open) => { if (!open) setDeletingRule(null) }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Delete Classification Rule</DialogTitle>
            <DialogDescription>
              This will permanently delete this classification rule. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingRule(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Counterparty Mappings Tab ───────────────────────────────────────────────

function CounterpartyMappingsTab({ clients }: { clients: ClientSummary[] }) {
  const [mappings, setMappings] = useState<CounterpartyMapping[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [clientFilter, setClientFilter] = useState('')

  const [deletingMapping, setDeletingMapping] = useState<CounterpartyMapping | null>(null)
  const [deleting, setDeleting] = useState(false)

  const loadMappings = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getCounterpartyMappings({
        search: debouncedSearch || undefined,
        client_id: clientFilter || undefined,
        page,
        page_size: PAGE_SIZE,
      })
      setMappings(data.mappings)
      setTotal(data.total)
    } catch {
      showErrorToast('Failed to load counterparty mappings.')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, clientFilter, page])

  useEffect(() => { loadMappings() }, [loadMappings])
  useEffect(() => { setPage(1) }, [debouncedSearch, clientFilter])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const handleDelete = async () => {
    if (!deletingMapping) return
    setDeleting(true)
    try {
      await deleteCounterpartyMapping(deletingMapping.id)
      showSuccessToast('Counterparty mapping deleted.')
      setDeletingMapping(null)
      loadMappings()
    } catch {
      showErrorToast('Failed to delete counterparty mapping.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search mappings..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-neutral-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Counterparty VAT</TableHead>
              <TableHead>Counterparty Name</TableHead>
              <TableHead>Mirage ID</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[60px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : mappings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                  No counterparty mappings found.
                </TableCell>
              </TableRow>
            ) : (
              mappings.map((mapping) => (
                <TableRow key={mapping.id}>
                  <TableCell className="font-medium">{mapping.client_name ?? '-'}</TableCell>
                  <TableCell className="font-mono text-sm">{mapping.counterparty_vat}</TableCell>
                  <TableCell>{mapping.counterparty_name_normalized}</TableCell>
                  <TableCell className="font-mono text-sm">{mapping.mirage_counterparty_id}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDisplayDate(mapping.created_at)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeletingMapping(mapping)}
                      title="Delete mapping"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-3 text-sm tabular-nums">{page} / {totalPages}</span>
            <Button variant="outline" size="icon" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingMapping} onOpenChange={(open) => { if (!open) setDeletingMapping(null) }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Delete Counterparty Mapping</DialogTitle>
            <DialogDescription>
              This will permanently delete the mapping for "{deletingMapping?.counterparty_name_normalized}". This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingMapping(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Client Identifications Tab ─────────────────────────────────────────────

function ClientIdentificationsTab({ clients }: { clients: ClientSummary[] }) {
  const [items, setItems] = useState<ClientIdentificationItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [clientFilter, setClientFilter] = useState('')
  const [deletingItem, setDeletingItem] = useState<ClientIdentificationItem | null>(null)
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getClientIdentifications({
        search: debouncedSearch || undefined,
        client_id: clientFilter || undefined,
        page,
        page_size: PAGE_SIZE,
      })
      setItems(data.identifications)
      setTotal(data.total)
    } catch {
      showErrorToast('Failed to load client identifications.')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, clientFilter, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(1) }, [debouncedSearch, clientFilter])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const handleDelete = async () => {
    if (!deletingItem) return
    setDeleting(true)
    try {
      await deleteClientIdentification(deletingItem.id)
      showSuccessToast('Client identification deleted.')
      setDeletingItem(null)
      load()
    } catch {
      showErrorToast('Failed to delete.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search identifiers..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm">
          <option value="">All clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="rounded-lg border border-neutral-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Identifier Value</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[60px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="h-32 text-center text-muted-foreground">Loading...</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="h-32 text-center text-muted-foreground">No client identifications found.</TableCell></TableRow>
            ) : items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.client_name ?? '-'}</TableCell>
                <TableCell><Badge variant="outline">{item.identifier_type}</Badge></TableCell>
                <TableCell className="font-mono text-sm">{item.identifier_value}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{formatDisplayDate(item.created_at)}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => setDeletingItem(item)} title="Delete"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}</p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="px-3 text-sm tabular-nums">{page} / {totalPages}</span>
            <Button variant="outline" size="icon" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      <Dialog open={!!deletingItem} onOpenChange={(open) => { if (!open) setDeletingItem(null) }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Delete Client Identification</DialogTitle>
            <DialogDescription>This will permanently delete the identifier "{deletingItem?.identifier_value}". This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingItem(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function LearningsPage() {
  const [clients, setClients] = useState<ClientSummary[]>([])

  useEffect(() => {
    fetchClients().then(setClients).catch(() => {
      // Clients are optional for filtering — fail silently
    })
  }, [])

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2 page-main-heading">
          Rules &amp; Mappings
        </h2>
        <p className="text-sm text-muted-foreground">
          Manage learned classification rules and counterparty mappings used by the AI during invoice processing.
        </p>
      </div>

      <Tabs defaultValue="rules" className="w-full">
        <TabsList>
          <TabsTrigger value="rules">Classification Rules</TabsTrigger>
          <TabsTrigger value="mappings">Counterparty Mappings</TabsTrigger>
          <TabsTrigger value="identifications">Client Identifications</TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="mt-4">
          <ClassificationRulesTab clients={clients} />
        </TabsContent>

        <TabsContent value="mappings" className="mt-4">
          <CounterpartyMappingsTab clients={clients} />
        </TabsContent>

        <TabsContent value="identifications" className="mt-4">
          <ClientIdentificationsTab clients={clients} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default LearningsPage
